import { Col, Dropdown, Row } from 'antd';
import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { ArrowDownIcon } from 'components/Icons';
import {
  BUSINESS_TYPE_COLLECTION,
  BUSINESS_TYPE_ENUM,
  SkuGalleryLabelCount,
} from 'domains/gallerySkuDomain';
import { JobConfigSourceEnum } from 'domains/jobconfigDomain';
import { usePage } from 'hooks';
import { sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { ALBERTSONS_ORGS } from 'utils/constants';
import { startJobConfigWithGalleryLabel } from 'utils/gallery';
import './Shop.less';

interface Props {
  children?: React.ReactNode;
}

const Shop = (props: Props) => {
  const { children } = props;
  const { t } = useTranslation();
  const { galleryLabels: allGalleryLabels, companyLabels: allCompanyLabels } =
    useAppSelector((state) => state.gallery);
  const dispatch = useAppDispatch();
  const products = sortBy(
    allGalleryLabels.filter(
      (item) =>
        !item.skuLabel.parentId && item.skuCount > 0 && item.skuLabel.active
    ),
    (item) => {
      return item.skuLabel.name;
    }
  );
  const comLab = sortBy(
    allCompanyLabels.filter((i) => i.skuCount > 0 && i.skuLabel.active),
    (i) => i.skuLabel.name
  );
  const { isShopPage } = usePage();
  const [visible, setVisible] = useState(false);

  return (
    <Dropdown
      placement="bottomCenter"
      visible={visible}
      onVisibleChange={(val) => setVisible(val)}
      overlay={
        <ShopFilter
          galleryLabels={products}
          companyLabels={comLab}
          onClick={() => {
            setVisible(false);
            dispatch.project.setJobProjectId(undefined);
          }}
        />
      }
      overlayClassName="shop-filter-overlay"
      // getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
    >
      {children ? (
        children
      ) : (
        <div className={classNames('shop', { active: isShopPage })}>
          {t('header.shop.label')} <ArrowDownIcon />
        </div>
      )}
    </Dropdown>
  );
};

const ShopFilter: React.FC<{
  galleryLabels: SkuGalleryLabelCount[];
  companyLabels: SkuGalleryLabelCount[];
  onClick: () => void;
}> = ({ galleryLabels, companyLabels, onClick }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const showCompanyList = companyLabels.length > 0;
  const orgId = useAppSelector((state) => state.app.orgId);

  const galleryLabelsSku = useMemo(() => {
    return {
      preConfigSku: (galleryLabels || [])
        .filter((item) =>
          item.skuLabel.businessTypes.includes(BUSINESS_TYPE_ENUM.MY_COMPANY)
        )
        .map((item) => {
          return {
            id: item.skuLabel.id,
            name: item.skuLabel.name,
            ...item,
          };
        }),
      sku: (galleryLabels || []).filter(
        (item) =>
          !item.skuLabel.businessTypes.includes(BUSINESS_TYPE_ENUM.MY_COMPANY)
      ),
    };
  }, [galleryLabels]);

  const companyLabelsSku = useMemo(() => {
    return {
      preConfigSku: companyLabels.map((i) => {
        return {
          id: i.skuLabel.id,
          name: i.skuLabel.name,
          ...i,
        };
      }),
      sku: companyLabels.filter((i) => i.skuCount) || [],
    };
  }, [companyLabels]);

  return (
    <div
      className={classNames('ant-dropdown-menu shop-filter', {
        incomplete: !showCompanyList,
      })}
    >
      <Row style={{ width: '100%' }}>
        <Col
          span={showCompanyList ? 14 : 19}
          style={{ maxHeight: '500px', overflowY: 'auto' }}
        >
          <div className="shop-filter-products">
            <FilterBlock
              title={t(
                ALBERTSONS_ORGS.includes(orgId)
                  ? 'header.shop.albertsonProducts'
                  : 'header.shop.products'
              )}
              filters={galleryLabelsSku.sku.map((item) => item.skuLabel)}
              type="gallery"
              link={routesMap.gallery.path}
              onItemClick={(id) => {
                const item = galleryLabelsSku.sku.find(
                  (item) => item.skuLabel.id === id
                );
                dispatch.jobConfig.setJobconfigSource(JobConfigSourceEnum.SHOP);
                if (item) {
                  startJobConfigWithGalleryLabel(dispatch, item, history);
                }

                if (onClick) {
                  onClick();
                }
              }}
            />
          </div>
        </Col>
        <Col className="shop-filter-col" span={5}>
          <div className="shop-filter-business-type">
            <FilterBlock
              title={t('header.shop.businessType')}
              filters={BUSINESS_TYPE_COLLECTION}
              type="business"
              link=""
              onItemClick={() => {
                dispatch.jobConfig.setJobconfigSource(JobConfigSourceEnum.SHOP);
                if (onClick) {
                  onClick();
                }
              }}
            />
          </div>
        </Col>
        {showCompanyList && (
          <Col className="shop-filter-col" span={5}>
            <div className="shop-filter-my-company-type">
              <FilterBlock
                title={t(
                  ALBERTSONS_ORGS.includes(orgId)
                    ? 'header.shop.albertsonMyCompany'
                    : 'header.shop.myCompany'
                )}
                filters={companyLabelsSku.sku.map((item) => item.skuLabel)}
                type="company"
                link={routesMap.companyGallery!.path}
                onItemClick={(id) => {
                  const curLabel =
                    companyLabelsSku.preConfigSku.find(
                      (item) => item.id === id
                    ) || null;
                  dispatch.jobConfig.setJobconfigSource(
                    JobConfigSourceEnum.SHOP
                  );
                  dispatch.companyGallery.setSelectedSkuType(curLabel);
                  history.push(routesMap.companyGallery!.path);
                  if (onClick) {
                    onClick();
                  }
                }}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

const FilterBlock = ({
  title,
  filters,
  link,
  type,
  onItemClick,
}: {
  title: string;
  filters: { id: number | string; name: string }[];
  link: string;
  type: 'gallery' | 'business' | 'company';
  onItemClick?: (id: number) => void;
}) => {
  const isGalleryType = type === 'gallery';
  const isBusinessType = type === 'business';
  const history = useHistory();
  const { isGalleryPage, isJobConfigPage, isCompanyGalleryPage } = usePage();
  const galleryLabel = useAppSelector(
    (state) =>
      state.jobConfig.galleryLabel || state.jobConfig.curSelectedGalleryLabel
  );
  const allGalleryLabels = useAppSelector(
    (state) => state.gallery.galleryLabels
  );
  const selectedBusinessType = useAppSelector(
    (state) => state.gallery.selectedBusinessType
  );
  const selectedCompanyLabel = useAppSelector(
    (state) => state.companyGallery.selectedSkuType
  );

  let isTitleActive = false;
  if (isGalleryPage && isGalleryType && !selectedBusinessType) {
    isTitleActive = true;
  }

  const myProductionsShow = useMemo(() => {
    return Boolean(
      allGalleryLabels?.filter((item) => {
        return (
          item.skuLabel.businessTypes.includes(
            BUSINESS_TYPE_ENUM.MY_PRODUCTS
          ) && item.skuCount > 0
        );
      }).length
    );
  }, [allGalleryLabels]);

  return (
    <div
      className={classNames('filter-block', {
        active: isTitleActive,
      })}
    >
      <div
        className="filter-block-title"
        onClick={() => {
          if (onItemClick) {
            onItemClick(0);
          }
        }}
      >
        {link ? (
          <Link
            to={link}
            className={classNames('filter-block-title-link', {
              active: isTitleActive,
            })}
          >
            {title}
          </Link>
        ) : (
          title
        )}
      </div>
      <Row>
        {(filters || [])
          .filter((item) => {
            if (item.id === BUSINESS_TYPE_ENUM.MY_PRODUCTS) {
              return myProductionsShow;
            } else {
              return true;
            }
          })
          .map((filter) => {
            let isItemActive = false;

            if (
              isGalleryPage &&
              isBusinessType &&
              selectedBusinessType === filter.id
            ) {
              isItemActive = true;
            } else if (
              isJobConfigPage &&
              galleryLabel?.skuLabel.id === filter.id &&
              !isTitleActive &&
              !selectedBusinessType
            ) {
              isItemActive = true;
            } else if (
              selectedCompanyLabel?.id === filter.id &&
              selectedCompanyLabel.name === filter.name &&
              isCompanyGalleryPage
            ) {
              isItemActive = true;
            }

            const menuItem = (
              <div
                className={classNames('filter-block-item', {
                  active: isItemActive,
                })}
                title={filter.name}
                onClick={() => {
                  if (onItemClick) {
                    onItemClick(filter.id as number);
                  }
                  if (isGalleryType) {
                    // go to job config
                  } else if (isBusinessType) {
                    history.push(
                      routesMap.gallery.path + `/business/${filter.id}`
                    );
                  }
                }}
              >
                {filter.name}
              </div>
            );

            return isGalleryType ? (
              <div className="gallery-item-wrap" key={filter.id}>
                {menuItem}
              </div>
            ) : (
              <Col span={24} key={filter.id}>
                {menuItem}
              </Col>
            );
          })}
      </Row>
    </div>
  );
};

export default Shop;
